import { differenceBy, pullAllBy } from "lodash";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { allowsNewValues } from "PFCore/helpers/custom_type";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import {
  CurrentProfile,
  CustomField,
  CustomType,
  ESSENTIAL_SKILL_WEIGHT,
  FeatureFlag,
  Keyword,
  PureCustomValue,
  SUPPORTING_SKILL_WEIGHT,
  Weight
} from "PFTypes";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { MatchAttributes } from "../../components/match_attributes";
import { ComponentProps } from "./component_prop_types";

type SkillsCustomValuesEditFieldWrapperProps = ComponentProps & {
  value: PureCustomValue[];
  error: Record<string, any>[] | string;
  customType: CustomType;
  connectedFields: CustomField[];
  currentProfile: CurrentProfile;
  qaIdPrefix: string;
  inputStyle: Record<string, any>;
  name?: string;
  description?: string;
  usedOn: "Activity_edition" | "Activity_creation";
};

export const SkillsCustomValuesEditFieldWrapper = ({
  property,
  handleChange,
  value,
  error,
  customType,
  connectedFields,
  currentProfile,
  name,
  description,
  qaIdPrefix,
  required,
  usedOn
}: SkillsCustomValuesEditFieldWrapperProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "edit.sections.match" });

  const isEnabled = useIsFeatureEnabled();

  const essentialSkills = value.filter(({ weight }) => weight === ESSENTIAL_SKILL_WEIGHT);
  const supportingSkills = value.filter(({ weight }) => weight === SUPPORTING_SKILL_WEIGHT);

  const activityCanCreateValues = isEnabled(FeatureFlag.ActivityCanCreateValues);

  const getHandleSkillsChangeFunction = useCallback(
    (weight: Weight = null) =>
      (values: PureCustomValue[]) => {
        const valuesWithSameWeight = value.filter((value) => value.weight === weight);
        if (valuesWithSameWeight.length > values.length) {
          const removedValues = differenceBy(valuesWithSameWeight, values, "id");

          handleChange(pullAllBy(value, removedValues), property);
        }
        const newValues = differenceBy(values, valuesWithSameWeight, "id").map((val) => ({ ...val, weight }));

        handleChange([...value, ...newValues], property);
      },
    [handleChange, property, value]
  );

  const handleEssentialSkillsChange = useCallback(getHandleSkillsChangeFunction(5), [
    getHandleSkillsChangeFunction
  ]);
  const handleSupportingSkillsChange = useCallback(getHandleSkillsChangeFunction(null), [
    getHandleSkillsChangeFunction
  ]);

  const handleRelevantAttributeChange = (
    values: (Keyword | PureCustomValue)[],
    isEssential: boolean = false
  ) => {
    const currentValueIds = value.map(({ id }) => id);
    const formattedValues = values
      .map((value) => ({
        ...value,
        text: String("value" in value ? value.value : value.text),
        weight: value.weight || null
      }))
      .map((val) =>
        currentValueIds.includes(val.id) ? val : { ...val, weight: isEssential ? 5 : null }
      ) as PureCustomValue[];

    handleChange(formattedValues);
  };

  return (
    <>
      <CustomValuesEditField
        label={t("essentialSkills")}
        tip=""
        customType={customType}
        connectedFields={connectedFields}
        values={essentialSkills}
        multi={property.kind === "multiple"}
        qaIdPrefix={`${qaIdPrefix}_essential`}
        adminPage={false}
        errors={error}
        letClear={true}
        letCreate={activityCanCreateValues && allowsNewValues(customType)}
        handleChange={handleEssentialSkillsChange}
        filterOptions={(options) => differenceBy(options, supportingSkills, "id")}
        required={required}
        hasValuesWithExpiryDate={false}
        isActivity
      />

      {property.match && (
        <MatchAttributes
          name={name}
          description={description}
          attributes={value}
          onMatchAttributeChange={(values) => handleRelevantAttributeChange(values, true)}
          userId={currentProfile.id}
          usedOn={usedOn}
          type={customType.name}
        />
      )}

      <CustomValuesEditField
        label={t("supportingSkills")}
        tip=""
        customType={customType}
        connectedFields={connectedFields}
        values={supportingSkills}
        multi={property.kind === "multiple"}
        qaIdPrefix={qaIdPrefix}
        adminPage={false}
        letClear={true}
        letCreate={activityCanCreateValues && allowsNewValues(customType)}
        handleChange={handleSupportingSkillsChange}
        filterOptions={(options) => differenceBy(options, essentialSkills, "id")}
        hasValuesWithExpiryDate={false}
        isActivity
      />

      {property.match && (
        <MatchAttributes
          name={name}
          description={description}
          attributes={value}
          onMatchAttributeChange={(values) => handleRelevantAttributeChange(values)}
          userId={currentProfile.id}
          usedOn={usedOn}
          type={customType.name}
        />
      )}
    </>
  );
};
